import React, { useMemo } from 'react';

import { FormProps } from '@components/admin/FormDialog';
import { FormAlert } from '@components/Forms/FormAlert';
import { ManagementTextInput } from '@components/Forms/ManagementTextInput';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { usePatchBookingInquiry } from '@hooks/mutation/dashboard/usePatchBookingInquiry';
import { AdminInquiryDetail, bookingInquiryForm } from '@models/BookingInquiry';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate } from '@utils/forms';
import { Form, Formik } from 'formik';
import { isLeft } from 'fp-ts/lib/Either';
import * as t from 'io-ts';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    paddingBottom: 40,
  },
}));

const customerNoSchema = t.type({
  customer_no: bookingInquiryForm.props.customer_no,
});

const validate = createValidate(customerNoSchema);

export interface BookingInquiryCustomerNoFormProps extends FormProps {
  adminInquiryDetail: AdminInquiryDetail;
}

export const BookingInquiryCustomerNoForm: React.FC<BookingInquiryCustomerNoFormProps> = (
  props,
) => {
  const { formikRef, adminInquiryDetail } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const mutation = usePatchBookingInquiry(adminInquiryDetail.d2d_no);

  const initialValues: t.TypeOf<typeof customerNoSchema> = useMemo(
    () => ({
      customer_no: adminInquiryDetail.customer_no,
    }),
    [adminInquiryDetail],
  );

  return (
    <Formik<typeof initialValues>
      innerRef={formikRef}
      initialValues={initialValues}
      validate={validate}
      onSubmit={async (values) => {
        const decoded = customerNoSchema.decode(values);
        if (isLeft(decoded)) {
          throw new Error('Unexpected error in decoding. ');
        }

        await mutation.mutateAsync(decoded.right);
      }}
    >
      <Form>
        <SubmittingOverlay />
        <FormAlert mutation={mutation} />

        <div className={classes.container}>
          <ManagementTextInput name="customer_no" label={bookingInquiryStrings.customerNo} />
        </div>
      </Form>
    </Formik>
  );
};
